import React, { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { Link, navigate } from 'raviger';
import { Card, Col, ListGroup, Row, Spinner } from 'react-bootstrap';
import Moment from 'react-moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft
} from '@fortawesome/free-solid-svg-icons';
import Header from '../components/Header';
import { SystemErrorContext } from '../components/SystemError';
import AnimalCards from '../components/AnimalCards';
import ShelterlyPrintifyButton from '../components/ShelterlyPrintifyButton';
import { useLocationWithRoutes } from '../hooks';
import LoadingLink from "../components/LoadingLink";
import ActionsDropdown from '../components/ActionsDropdown';
import {
  printIntakeSummaryAnimalCareSchedules,
  printOwnerDetails,
  printAllOwnersDetails,
  printIntakeSummary
} from './Utils';

function ShelterIntakeSummary({ id, incident, organization }) {
  const { setShowSystemError } = useContext(SystemErrorContext);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({
    id: '',
    shelter: '',
    shelter_name: '',
    intake_type: '',
    date: '',
    animals: [],
    animal_objects: [],
    animal_count: 0,
    person: null,
    person_object: {first_name:'', last_name:''}
  });
  const [organizationData, setOrganizationData] = useState({
    name: '',
    short_name: '',
    liability_name: '',
    liability_short_name: '',
  });
  const [animalOwners, setAnimalOwners] = useState([]);
  const { getFullLocationFromPath } = useLocationWithRoutes();

  function buildAnimalUrl(animal) {
    return getFullLocationFromPath(`/${organization}/${incident}/animals/${animal.id_for_incident}`)
  }

  const handlePrintOwnerClick = () =>
    printOwnerDetails(data.person_object, organizationData);

  const handlePrintAnimalOwnersClick = () =>
    printAllOwnersDetails(animalOwners, organizationData);

  const handlePrintAllAnimalsClick = async () => {
    setIsLoading(true);
    const animals = data.animal_objects.map((animal) => ({
      ...animal,
      url: buildAnimalUrl(animal)
    }))
    return printIntakeSummaryAnimalCareSchedules(animals, id)
    .finally(() => setIsLoading(false));;
  }

  const handlePrintIntakeSummary = async () =>
    printIntakeSummary(data);

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchIntakeSummaryData = async () => {
      // Fetch intake summary data.
      await axios.get('/shelter/api/intakesummary/' + id + '/?incident=' + incident, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          setData(response.data);
          setIsLoading(false);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
          setIsLoading(false);
        }
      });
    };
    fetchIntakeSummaryData();

    const fetchOrganizationData = async () => {
      // Fetch Organization data.
      await axios.get('/incident/api/organization/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          setOrganizationData(response.data[0]);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    fetchOrganizationData();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id, incident]);

  useEffect(() => {
    const ownersFromAnimals = [];

    data?.animal_objects?.forEach?.((animal) => {
      animal.owners?.forEach?.((owner) => {
        const foundOwner = ownersFromAnimals.find(({ id }) => id === owner.id);
        if (!foundOwner) {
          ownersFromAnimals.push({
            ...owner,
            animals: data.animal_objects.filter(
              (a) => !!a.owners.find(({ id }) => id === owner.id)
            ),
          });
        }
      });
    });

    setAnimalOwners(ownersFromAnimals);
  }, [data]);

  return (
    <>
      <Header>
        <span style={{cursor:'pointer'}} onClick={() => navigate("/" + organization + "/" + incident + "/shelter/" + data.shelter)} className="me-2"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="sm" inverse /></span>
        {data.shelter_name} -&nbsp;
        Intake Summary
      </Header>
      <Row className="d-flex">
        <Col className="d-flex" xs="12">
          <Card className="border rounded" style={{width:"100%"}}>
            <Card.Body style={{marginTop:"-10px"}}>
              <div className="d-flex justify-content-between" style={{marginBottom:"-10px"}}>
                <h4 style={{paddingTop:"5px"}}>Information</h4>
              </div>
              <p className="phr" />
              <ListGroup variant="flush" style={{marginTop:"-13px", marginBottom:"-13px"}}>
                <ListGroup.Item>
                  <b>Type: </b>{data.intake_type === "owners_walkin"
                    ? "Owner Walk-In "
                    : data.intake_type === "reporter_walkin"
                    ? "Reporter Walk-In "
                    : "Field "}
                </ListGroup.Item>
                <ListGroup.Item>
                  <b>Date:</b> <Moment format="MMMM Do YYYY HH:mm">{data.date}</Moment>
                </ListGroup.Item>
                {data.intake_type === 'owners_walkin' ?
                  <ListGroup.Item>
                    <b>Owner:</b>{' '}
                    <Link
                      href={"/" + organization + "/" + incident + "/people/owner/" + data.person}
                      className="text-link"
                      style={{textDecoration:"none", color:"white"}}
                    >
                      {data.person_object.first_name}
                      {' '}
                      {data.person_object.last_name}
                      {' '}
                    </Link>
                    <ShelterlyPrintifyButton
                      id="dispatch-assignment-owner"
                      spinnerSize={0.8}
                      tooltipPlacement='top'
                      label='Download Owner Summary'
                      printFunc={handlePrintOwnerClick}
                    />
                  </ListGroup.Item>
                : data.intake_type === 'reporter_walkin' ?
                <ListGroup.Item>
                  <b>Reporter:</b> <Link href={"/" + organization + "/" + incident + "/people/reporter/" + data.person} className="text-link" style={{textDecoration:"none", color:"white"}}>{data.person_object.first_name} {data.person_object.last_name}</Link>
                </ListGroup.Item>
                : animalOwners.length ?
                  <ListGroup.Item>
                    <b>Owner(s):</b>{' '}
                    {animalOwners.map((owner, i) => (
                      <span key={`owner-${owner.id}`}>{owner.first_name} {owner.last_name}{i < animalOwners.length - 1 ? ', ' : ' '}</span>
                    ))}
                    <ShelterlyPrintifyButton
                      id="dispatch-assignment-all-owners"
                      spinnerSize={0.8}
                      tooltipPlacement='top'
                      tooltipText='Download All Owner Summaries'
                      printFunc={handlePrintAnimalOwnersClick}
                    />
                  </ListGroup.Item>
                : ""}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <div className="row mt-3">
        <div className="col-12 d-flex">
          <Card className="border rounded" style={{width:"100%"}}>
            <Card.Body style={{marginTop:"-10px", marginBottom:"-15px"}}>
              <div className="d-flex justify-content-between" style={{marginBottom:"-10px"}}>
                <h4 style={{paddingTop:"5px"}}>Animals ({data.animal_count})</h4>
                {isLoading ? (
                <div className="d-flex" style={{width:"148px", justifyContent:"center"}}>
                  <Spinner
                    {...{
                      as: 'span',
                      animation: 'border',
                      size: undefined,
                      role: 'status',
                      'aria-hidden': 'true',
                      variant: 'light',
                      style: {
                        height: '2rem',
                        width: '2rem',
                        marginBottom: '0.25rem'
                      }
                    }}
                  />
                </div>
              ) : (
                <ActionsDropdown variant="secondary">
                  <ShelterlyPrintifyButton
                    id="dispatch-assignment-animal-care-schedules"
                    spinnerSize={1.5}
                    tooltipPlacement='top'
                    label='Download All Animal Care Schedules'
                    printFunc={handlePrintAllAnimalsClick}
                    noOverlay={true}
                  />
                </ActionsDropdown>
              )}
              </div>
              <p className="phr" />
              <AnimalCards animals={data.animal_objects} show_owner={true} organization={organization} incident={"/" + incident} />
              {data.animals.length < 1 ? <p>No animals were intaken.</p> : ""}
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ShelterIntakeSummary;
